// Location for the field photos app. Crews can't use the app without it: every photo is stamped
// with where it was taken, and jobs are matched by distance. The layout blocks the app until
// `status` is 'granted'.

export type FieldLocationStatus =
  'idle' | 'checking' | 'granted' | 'denied' | 'unavailable' | 'unsupported'

let generation = 0
let watchId: number | null = null
let permissionStatus: PermissionStatus | null = null

export function useFieldLocation() {
  const status = useState<FieldLocationStatus>(
    'field-location-status',
    () => 'idle'
  )
  const position = useState<FieldPosition | null>(
    'field-location-position',
    () => null
  )

  const clearWatch = () => {
    if (watchId !== null) navigator.geolocation.clearWatch(watchId)
    watchId = null
  }

  const stop = () => {
    generation++
    if (permissionStatus) permissionStatus.onchange = null
    permissionStatus = null
    clearWatch()
    position.value = null
    status.value = 'idle'
  }

  const watch = (version: number) => {
    if (version !== generation) return
    clearWatch()
    watchId = navigator.geolocation.watchPosition(
      ({ coords }) => {
        if (version !== generation) return
        position.value = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        }
        status.value = 'granted'
      },
      (error) => {
        if (version !== generation) return
        if (error.code === error.PERMISSION_DENIED) {
          // Location was refused or turned off: lock the app.
          clearWatch()
          position.value = null
          status.value = 'denied'
        } else if (!position.value) {
          // No fix yet (indoors, weak GPS). Keep watching; the gate explains what to do.
          status.value = 'unavailable'
        }
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
    )
  }

  const start = async () => {
    if (!import.meta.client) return
    stop()
    const version = generation
    if (!('geolocation' in navigator)) {
      status.value = 'unsupported'
      return
    }
    if (status.value !== 'granted') status.value = 'checking'

    // React when the worker changes the permission in the browser settings while the app is open.
    try {
      if (!permissionStatus && navigator.permissions?.query) {
        const permission = await navigator.permissions.query({
          name: 'geolocation',
        })
        if (version !== generation) return
        permissionStatus = permission
        permissionStatus.onchange = () => {
          if (permissionStatus?.state === 'denied') {
            clearWatch()
            position.value = null
            status.value = 'denied'
          } else {
            watch(version)
          }
        }
      }
    } catch {
      // Older browsers without the Permissions API: watchPosition still reports a refusal.
    }
    watch(version)
  }

  /** The current position, waiting briefly for a first fix if needed. */
  const current = async (timeout = 8000): Promise<FieldPosition | null> => {
    if (position.value) return position.value
    try {
      return await getPosition(timeout)
    } catch {
      return null
    }
  }

  return { current, position, start, status, stop }
}
